import { useContext, useState, useId } from 'react'
import PropTypes from 'prop-types'
import Section from './_Section'
import ButtonEl from '../helpers/ctas/Button'
import { PlusIcon, MinusIcon } from '@heroicons/react/24/solid'
import { transformPaddingToTailwind } from '../../helpers/style'
import themeConfig from '../../../theme.config'
import { MarkupContext } from '../../providers/Markup'
import { stripSingleParagraphWrapper, stripTitleHTML } from '../../helpers/markup'
import { dataLayerPush } from '../../helpers/thirdparty'

const ALIGNMENT = {
  CENTER: 'center',
  RIGHT: 'right',
  LEFT: 'left',
}

const CTA_ALIGNMENT = {
  CENTER: 'Center',
}

const GA_EVENTS = {
  ACCORDION_INTERACTION: 'accordion_interaction',
}

// --- AccordionItem -----------------------------------------------------------

function AccordionItem({ item, index, isOpen, headerId, panelId, alignment, onToggle, createMarkup }) {
  // Panel titles are rich text. Strip the single <p> the RTE wraps them in so the title stays
  // phrasing content inside the button, and give the data layer the plain text, not the markup.
  const titleMarkup = createMarkup(stripSingleParagraphWrapper(item.title))
  const titleText = titleMarkup ? stripTitleHTML(titleMarkup) : ''

  return (
    <li className="border-t border-(--color-border-default)">
      <button
        id={headerId}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className={`relative flex justify-between items-center text-${alignment} w-full py-6 group cursor-pointer hover:bg-(--color-border-default)/30 rounded transition-colors duration-300 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-text-highlight) focus-visible:z-10`}
        onClick={(e) => onToggle(index, titleText, e)}
      >
        <span
          className="text-base font-semibold leading-6 transition-colors duration-300 ease-in-out text-(--sb-common-base-body-text-color)!"
          data-rte="true"
          dangerouslySetInnerHTML={titleMarkup}
        ></span>

        <div className="relative ml-2 w-6 h-6 shrink-0" aria-hidden="true">
          <PlusIcon
            className={`absolute inset-0 w-6 h-6 text-(--sb-common-base-body-text-color) transition-opacity duration-300 ease-in-out ${isOpen ? 'opacity-0' : 'opacity-100'}`}
          />
          <MinusIcon
            className={`absolute inset-0 w-6 h-6 text-(--sb-common-base-body-text-color) transition-opacity duration-300 ease-in-out ${isOpen ? 'opacity-100' : 'opacity-0'}`}
          />
        </div>
      </button>

      <div
        id={panelId}
        role="region"
        aria-labelledby={headerId}
        className={`grid transition-[grid-template-rows] duration-500 ease-in-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
      >
        <div
          className={`overflow-hidden transition-opacity duration-500 ease-in-out ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
        >
          <div data-rte="true" dangerouslySetInnerHTML={createMarkup(item.content)} className="pb-6" />
        </div>
      </div>
    </li>
  )
}

AccordionItem.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  headerId: PropTypes.string.isRequired,
  panelId: PropTypes.string.isRequired,
  alignment: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
  createMarkup: PropTypes.func.isRequired,
}

// --- Accordion ---------------------------------------------------------------

function Accordion(props) {
  const { createMarkup } = useContext(MarkupContext)

  const defaultFirstOpen = props.defaultFirstOpen ?? false
  const allowMultipleOpen = props.allowMultipleOpen ?? !(themeConfig.settings?.onlyOneAccordionOpen ?? false)

  const [open, setOpen] = useState(() => (defaultFirstOpen ? { 0: true } : {}))

  const componentPadding = transformPaddingToTailwind(props.componentPadding)
  const alignment = props.contentAlignment?.toLowerCase() || ALIGNMENT.LEFT
  const textAlignClass =
    alignment === ALIGNMENT.CENTER ? 'text-center' : alignment === ALIGNMENT.RIGHT ? 'text-right' : 'text-left'
  const ctaAlignClass = props.ctaLinkAlignment === CTA_ALIGNMENT.CENTER ? 'justify-center' : 'justify-start'

  const uniqueId = useId()

  function handleToggle(index, title, e) {
    dataLayerPush(
      {
        event: GA_EVENTS.ACCORDION_INTERACTION,
        action: open[index] ? 'close' : 'open',
        click_text: title,
        link_position: String(index + 1),
      },
      e.currentTarget
    )

    setOpen((previousOpen) => {
      if (!allowMultipleOpen) {
        const wasOpen = previousOpen[index]
        const newState = Object.fromEntries(Object.keys(previousOpen).map((k) => [k, false]))
        newState[index] = !wasOpen
        return newState
      }
      return { ...previousOpen, [index]: !previousOpen[index] }
    })
  }

  return (
    <Section type="accordion" outerClass={componentPadding} innerClass={`px-7 lg:px-8 ${textAlignClass}`} {...props}>
      {props.accordionPanelItems?.length > 0 && (
        <ul
          className={`border-b border-(--color-border-default) ${props.title || props.description ? 'mt-6' : ''} text-${alignment}`}
        >
          {props.accordionPanelItems.map((item, i) => (
            <AccordionItem
              key={item.title ?? i}
              item={item}
              index={i}
              isOpen={!!open[i]}
              headerId={`${uniqueId}-header-${i}`}
              panelId={`${uniqueId}-panel-${i}`}
              alignment={alignment}
              onToggle={handleToggle}
              createMarkup={createMarkup}
            />
          ))}
        </ul>
      )}
      {props.ctaLink?.length > 0 && (
        <div data-type="cta-links" className={`flex flex-wrap ${ctaAlignClass} gap-4 max-sm:gap-3`}>
          {props.ctaLink.map((ctaLinkItem, i) => (
            <ButtonEl
              key={i}
              link={ctaLinkItem.link}
              theme={ctaLinkItem.linkType.toLowerCase()}
              icon={ctaLinkItem.linkIconClass}
              position={ctaLinkItem.linkPosition}
            />
          ))}
        </div>
      )}
    </Section>
  )
}

Accordion.propTypes = {
  defaultFirstOpen: PropTypes.bool,
  allowMultipleOpen: PropTypes.bool,
  componentPadding: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  contentAlignment: PropTypes.string,
  ctaLinkAlignment: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  accordionPanelItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.string,
    })
  ),
  ctaLink: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.object,
      linkType: PropTypes.string,
      linkIconClass: PropTypes.string,
      linkPosition: PropTypes.string,
    })
  ),
}

export default Accordion
